import { useMemo } from 'react';

import { format, parseISO } from 'date-fns';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { DailyCount } from '@/types/service/internalAnalytics';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { CHART_COLORS } from '@/utils/saasDashboardHelpers';

interface OpportunityTrendChartProps {
  data: DailyCount[];
}

const OpportunityTrendChart = ({ data }: OpportunityTrendChartProps) => {
  // Transform data for display
  const chartData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      displayDate: format(parseISO(item.date), 'MMM dd'),
    }));
  }, [data]);

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>New Opportunities Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[300px] flex items-center justify-center text-muted-foreground'>
            No trend data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>New Opportunities Trend (Last 60 Days)</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width='100%' height={300}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis dataKey='displayDate' tick={{ fontSize: 12 }} interval='preserveStartEnd' />
            <YAxis />
            <Tooltip
              labelFormatter={(label) => `Date: ${label}`}
              formatter={(value) => [`${Number(value).toLocaleString()}`, 'New Opportunities']}
            />
            <Legend />
            <Line
              type='monotone'
              dataKey='count'
              name='New Opportunities'
              stroke={CHART_COLORS.line}
              strokeWidth={2}
              dot={{ fill: CHART_COLORS.line, strokeWidth: 2, r: 3 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default OpportunityTrendChart;
